import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import ArrowIcon from '../../../../assets/icons/ArrowIcon';
import { MyTasksWidgetProps } from './types';
import {
  Headline,
  MyTask,
  MyTasksText,
  TitleContainer,
  WidgetContainer,
} from './styles';
import ActionButton from '../../../components/ActionButton/ActionButton';
import { isTaskActive } from '../../../../helpers/dateTimeHelper';
import {
  cropConnectApiClient,
  CropConnectRoleDefinitions,
  CropConnectTaskDto,
  TaskType,
} from '../../../../services/cropConnectApiClient';
import { useContextStore } from '../../../../stores/contextStore';
import { securityTrimConditionsMet } from '../../../../services/auth/SecurityTrim';
import { useI18n } from '../../../../hooks/useI18n';
import { TranslationKeys } from '../../../../i18n/locales/defaultResources';
import { handleGeneralDocumentInMyTaskPreview } from '../../../../helpers/filePreviewHelper';

const MyTasksWidget: React.FC<MyTasksWidgetProps> = () => {
  const { t } = useI18n();

  const navigate = useNavigate();
  const {
    globalAssignment,
    nordzuckerInternalUser,
    businessPartnerAssignment,
    isAccessOnBehalf,
    activeBpNumber,
    isContextReady,
  } = useContextStore();

  const [tasks, setTasks] = useState<CropConnectTaskDto[]>([]);
  const [isAdminView, setIsAdminView] = useState(false);

  const handleTaskClicked = (task: CropConnectTaskDto) => {
    if (task.type === TaskType.EXTERNAL_URL) {
      if (task.propertyBag?.externalUrl) {
        window.open(task.propertyBag?.externalUrl, '_blank');
      }
    } else if (task.type === TaskType.ESHOP) {
      const eShopUrl = process.env.REACT_APP_ESHOP_URL;
      if (eShopUrl) {
        const encodedReturnUrl = encodeURIComponent(window.location.href);
        const fullUrl = `${eShopUrl}${encodedReturnUrl}`;
        window.open(fullUrl, '_self');
      }
    } else if (task.type === TaskType.INTERNAL_URL) {
      if (task.propertyBag?.internalUrl) {
        const url = new URL(task.propertyBag.internalUrl);
        navigate(url.pathname);
      }
    } else if (task.type === TaskType.SURVEY) {
      if (task.propertyBag?.Url) {
        window.open(task.propertyBag?.Url, '_blank');
      }
    } else if (task.type === TaskType.FILL_RED_CERT_SURVEY) {
      if (task.propertyBag?.surveyId) {
        navigate(`/red-cert?surveyId=${task.propertyBag.surveyId}`);
      } else {
        const surveyId = tasks.find(
          (t) =>
            t.type === TaskType.FILL_RED_CERT_SURVEY && t.propertyBag?.surveyId
        )?.propertyBag?.surveyId;
        if (surveyId) {
          navigate(`/red-cert?surveyId=${surveyId}`);
        }
      }
    } else if (task.type === TaskType.GENERAL_DOCUMENT) {
      if (task.propertyBag?.generalDocumentEcmId) {
        handleGeneralDocumentInMyTaskPreview(
          task.propertyBag?.generalDocumentEcmId
        );
      }
    } else if (task.type === TaskType.ECONTRACTING) {
      navigate('/e-contracting');
    } else if (task.type === TaskType.FIELD_MAP) {
      navigate('/field-nomination/cropSeasons');
    }
  };

  useEffect(() => {
    if (isContextReady()) {
      setTimeout(() => {
        setIsAdminView(
          !isAccessOnBehalf &&
            securityTrimConditionsMet(
              {
                requiredGlobalRoles: [CropConnectRoleDefinitions.TaskCreator],
              },
              globalAssignment,
              businessPartnerAssignment,
              nordzuckerInternalUser
            )
        );
      }, 1000);
    }
  }, [globalAssignment]);

  useEffect(() => {
    const fetchTasks = async () => {
      const tasks = await cropConnectApiClient.getTasks();
      setTasks(tasks.data || []);
    };
    fetchTasks();
    if (isContextReady()) {
      setIsAdminView(
        !isAccessOnBehalf &&
          securityTrimConditionsMet(
            {
              requiredGlobalRoles: [CropConnectRoleDefinitions.TaskCreator],
            },
            globalAssignment,
            businessPartnerAssignment,
            nordzuckerInternalUser
          )
      );
    }
  }, [activeBpNumber, isContextReady]);

  return (
    <>
      {(tasks.filter((task) => isTaskActive(task) && task.propertyBag?.headline)
        .length > 0 ||
        isAdminView) && (
        <WidgetContainer>
          <TitleContainer>
            <Headline>{t(TranslationKeys.my_tasks_widget_title)}</Headline>
            {isAdminView && (
              <ActionButton
                variant="accept-blue"
                buttonText={t(TranslationKeys.my_tasks_widget_manage_button)}
                onClickAction={() => navigate('/admin/my-tasks')}
              />
            )}
          </TitleContainer>
          <div>
            {tasks
              .filter(
                (task) => isTaskActive(task) && task.propertyBag?.headline
              )
              .sort(
                (a, b) =>
                  new Date(a.endDate).getTime() - new Date(b.endDate).getTime()
              )
              .map((task, index) => (
                <MyTask key={index} onClick={() => handleTaskClicked(task)}>
                  <div>
                    <MyTasksText>{task.propertyBag?.headline}</MyTasksText>
                    <div>{task.propertyBag?.description}</div>
                  </div>
                  <a onClick={() => {}}>
                    <ArrowIcon />
                  </a>
                </MyTask>
              ))}
          </div>
        </WidgetContainer>
      )}
    </>
  );
};

export default MyTasksWidget;
